import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Sparkles, Loader2, Trash2, Zap, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_COMMANDS = [
  { label: 'Reconcile Bank', command: 'Reconcile my bank' },
  { label: 'Process Payroll', command: 'Process payroll' },
  { label: 'Expense Summary', command: 'Show expense summary' },
  { label: 'Pending Approvals', command: 'Show pending approvals' },
  { label: 'AR Aging', command: 'Show AR aging report' },
];

const AIChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm **SkyBot**, your AI accounting assistant. I can execute commands and answer questions.\n\n**Try these commands:**\n• \"Reconcile my bank\"\n• \"Process payroll\"\n• \"Show expense summary\"\n• \"Show pending approvals\"\n\nOr just ask me anything about your accounting!",
        suggestions: QUICK_COMMANDS.map(c => c.command)
      }]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async (messageText) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/skybot/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      const data = await response.json();

      const assistantMessage = {
        role: 'assistant',
        content: data.message,
        action_taken: data.action_taken,
        data: data.data,
        suggestions: data.suggestions
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared! How can I help you today?",
      suggestions: QUICK_COMMANDS.map(c => c.command)
    }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderMessage = (message, idx) => {
    const isUser = message.role === 'user';

    return (
      <div
        key={idx}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
      >
        <div
          className={`max-w-[85%] rounded-lg px-4 py-2.5 ${
            isUser
              ? 'bg-sky-600 text-white'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {/* Action badge */}
          {message.action_taken && (
            <div className="flex items-center gap-1 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                {message.action_taken.replace(/_/g, ' ')}
              </Badge>
            </div>
          )}

          {/* Message content */}
          <div className="text-sm whitespace-pre-wrap">
            {message.content.split('\n').map((line, i) => {
              // Handle markdown-style bold
              const parts = line.split(/\*\*(.*?)\*\*/);
              return (
                <p key={i} className={i > 0 ? 'mt-1' : ''}>
                  {parts.map((part, j) => 
                    j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                  )}
                </p>
              );
            })}
          </div>

          {/* Data display */}
          {message.data && !isUser && (
            <div className="mt-3 p-2 bg-white/80 rounded border text-xs">
              {message.data.by_category && (
                <div className="space-y-1">
                  {Object.entries(message.data.by_category).slice(0, 5).map(([cat, amt]) => (
                    <div key={cat} className="flex justify-between">
                      <span className="text-gray-600">{cat}</span>
                      <span className="font-medium">${amt.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
              {message.data.aging && (
                <div className="space-y-1">
                  {Object.entries(message.data.aging).map(([period, amt]) => (
                    <div key={period} className="flex justify-between">
                      <span className="text-gray-600">{period.replace('_', '-')} days</span>
                      <span className="font-medium">${amt.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Suggestions */}
          {message.suggestions && message.suggestions.length > 0 && !isUser && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {message.suggestions.slice(0, 4).map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(suggestion)}
                  className="text-xs px-2 py-1 bg-sky-100 text-sky-700 rounded-full hover:bg-sky-200 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-20 right-4 w-96 h-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col z-50" data-testid="ai-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-sky-500 to-sky-600 rounded-t-xl">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-white" />
          <span className="font-semibold text-white">SkyBot</span>
          <Badge variant="secondary" className="text-xs bg-white/20 text-white border-0">
            AI Assistant
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearChat}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded transition-colors"
            title="Clear chat"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded transition-colors"
            data-testid="close-chat-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Quick Commands */}
      <div className="px-3 py-2 border-b bg-gray-50 flex gap-1.5 overflow-x-auto">
        {QUICK_COMMANDS.map((cmd, i) => (
          <button
            key={i}
            onClick={() => sendMessage(cmd.command)}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-white border rounded-full hover:bg-sky-50 hover:border-sky-300 transition-colors whitespace-nowrap"
          >
            <Zap className="w-3 h-3 text-sky-500" />
            {cmd.label}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.map((msg, idx) => renderMessage(msg, idx))}
        
        {loading && (
          <div className="flex justify-start mb-3">
            <div className="bg-gray-100 rounded-lg px-4 py-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
              <span className="text-sm text-gray-600">Processing...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a question or give a command..."
            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="bg-sky-600 hover:bg-sky-700"
            size="sm"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
